import type { TFunction } from "i18next";
import type { ApopiloKind, EventInfo, WallItem } from "../../api/content";

const SYMBOLS: Record<string, string> = { EUR: "€", USD: "$", GBP: "£" };

/**
 * Figma price chip: the currency trails the digits ("12€", "12.50€"); null price = free (no chip). `spaced` puts a
 * space before the symbol, as the Apopilo take sheet draws it ("80 €").
 */
export function formatPrice(priceCents: number | null, currency: string, opts: { spaced?: boolean } = {}): string | null {
  if (priceCents == null) return null;
  const whole = priceCents % 100 === 0;
  const amount = whole ? String(priceCents / 100) : (priceCents / 100).toFixed(2);
  const cur = currency?.toUpperCase();
  const symbol = SYMBOLS[cur];
  return symbol ? `${amount}${opts.spaced ? " " : ""}${symbol}` : `${amount}${cur ? ` ${cur}` : ""}`;
}

/** Apopilo units: Tutor counts days, Apopilo counts nights ("4 days", "1 night"). */
export function formatUnits(kind: ApopiloKind, units: number, t: TFunction): string {
  if (kind === "TUTOR") return t("apopilo_units_days", units === 1 ? "{{count}} day" : "{{count}} days", { count: units });
  return t("apopilo_units_nights", units === 1 ? "{{count}} night" : "{{count}} nights", { count: units });
}

/** The cover is the first photo of the post (photo URL, else its thumbnail). */
export function coverUrl(items: WallItem[]): string | null {
  for (const it of items) {
    if (it.type === "PHOTO" && (it.photoUrl || it.thumbnailUrl)) return it.photoUrl ?? it.thumbnailUrl;
  }
  return null;
}

/** Local-day key (YYYY-MM-DD) of an instant in `tz` (default: the browser's zone). */
export function localDayKey(iso: string, tz?: string): string {
  const parts = new Intl.DateTimeFormat("en-CA", { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date(iso));
  return parts;
}

/** Groups posts into viewer-local days, in input order (the server orders upcoming first). */
export function groupByLocalDay<T extends { event?: EventInfo }>(posts: T[], tz?: string): { day: string; startsAt: string; posts: T[] }[] {
  const groups: { day: string; startsAt: string; posts: T[] }[] = [];
  for (const p of posts) {
    if (!p.event) continue;
    const day = localDayKey(p.event.startsAt, tz);
    const last = groups[groups.length - 1];
    if (last && last.day === day) last.posts.push(p);
    else groups.push({ day, startsAt: p.event.startsAt, posts: [p] });
  }
  return groups;
}

const fmt = (iso: string, tz: string | undefined, o: Intl.DateTimeFormatOptions) => new Intl.DateTimeFormat(undefined, { timeZone: tz, ...o }).format(new Date(iso));
export const whenText = (iso: string, tz?: string) => fmt(iso, tz, { month: "long", day: "numeric", hour: "2-digit", minute: "2-digit" });
export const hourText = (iso: string, tz?: string) => fmt(iso, tz, { hour: "2-digit", minute: "2-digit" });
export const dayNumber = (iso: string, tz?: string) => fmt(iso, tz, { day: "numeric" });
export const monthShort = (iso: string, tz?: string) => fmt(iso, tz, { month: "short" });
export const dayHeaderText = (iso: string, tz?: string) => fmt(iso, tz, { weekday: "short", day: "numeric", month: "long" });

export type MarkerColor = "grey" | "yellow" | "green" | "blue" | "pink" | "red";

const HOUR = 3_600_000;

/** When an event is over: `endsAt`, else start + 3 h (same rule as the server and Android). */
export function eventEndMs(event: EventInfo): number {
  const start = new Date(event.startsAt).getTime();
  return event.endsAt ? new Date(event.endsAt).getTime() : start + 3 * HOUR;
}

/** True once the event is over (server-flagged `passed`, or the clock reached its end). */
export function isEventEnded(event: EventInfo, now: Date): boolean {
  return event.passed || now.getTime() >= eventEndMs(event);
}

/**
 * Map marker colour by time to start; red in the last hour and while running (R2). Null once ended: an ended event
 * has no marker (it leaves the map at the next minute tick, before the next fetch drops it).
 */
export function markerColorOf(event: EventInfo, now: Date): MarkerColor | null {
  if (isEventEnded(event, now)) return null;
  const start = new Date(event.startsAt).getTime();
  const t = now.getTime();
  if (t >= start) return "red";
  const h = (start - t) / HOUR;
  if (h > 12) return "grey";
  if (h > 9) return "yellow";
  if (h > 6) return "green";
  if (h > 3) return "blue";
  if (h > 1) return "pink";
  return "red";
}
